import React, { useState, useEffect } from "react";
import { UTILS, PBPUTILS } from "../config/API";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts",
  MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico",
  NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

// Helper to read StyleFolder from portal settings (same source as config.js)
const getStyleFolder = () => {
  try {
    const savedSettings = sessionStorage.getItem("portalSettings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      if (settings?.StyleFolder) {
        return settings.StyleFolder.toLowerCase();
      }
    }
  } catch (e) {
    // Ignore and fall back to empty string
  }
  return "";
};

const DisplayProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [styleFolder, setStyleFolder] = useState("");
  const navigate = useNavigate();

  // New function to fetch taxpayer information (same as in Home component)
  const fetchTaxPayerInfo = async (taxpayerId) => {
    try {
      const response = await fetch(
        PBPUTILS + `?taxpayer=${taxpayerId}&action=GetTaxPayerInfo`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching taxpayer information:", error);
      throw error;
    }
  };

  // Function to dispatch localStorage change event
  const dispatchLocalStorageChange = () => {
    window.dispatchEvent(new Event('localStorageChange'));
  };

  // Convert state code to full state name
  const getStateName = (code) => {
    return STATES[code?.toUpperCase()] || code;
  };

  // Ensure empty fields do not collapse height
  const displayData = (data) => {
    return data || <span>&nbsp;</span>;
  };

  // Helper function to parse Primary Contact (format: "Last, First") into firstName and lastName
  const parsePrimaryContact = (primaryContact) => {
    if (!primaryContact || typeof primaryContact !== "string") {
      return { firstName: "", lastName: "" };
    }
    const parts = primaryContact.split(",").map(part => part.trim());
    if (parts.length >= 2) {
      return {
        lastName: parts[0] || "",
        firstName: parts[1] || "",
      };
    }
    // If no comma, treat entire string as last name
    return {
      lastName: primaryContact,
      firstName: "",
    };
  };

  // Helper function to build profile data from business object
  const buildProfileData = (business, currentStyleFolder = null) => {
    // Use provided styleFolder or get from state/sessionStorage
    const folder = currentStyleFolder || styleFolder || getStyleFolder();
    const isCrit = folder === "crit";
    
    if (isCrit) {
      // For CRIT, use Primary Contact and PC fields
      const { firstName, lastName } = parsePrimaryContact(business["Primary Contact"] || "");
      
      return {
        firstName: firstName,
        middleName: "", // Always empty for CRIT
        lastName: lastName,
        address: business["Business Address"] || "",
        city: business["City"] || "",
        state: business["State"] || "",
        zipCode: business["Zip Code"] || "",
        phoneNumber: business["PC Phone #"] || "",
        email: business["PC Email Address"] || "",
        communityRegistration: business["Community Registration"] || "",
      };
    } else {
      // For non-CRIT, use existing Owner/Business fields
      return {
        firstName: business["Owner First Name"] || "",
        middleName: business["Owner Middle Name"] || "",
        lastName: business["Owner Last Name"] || "",
        address: business["Business Address "] || business["Business Address"] || "",
        city: business["Business City"] || business["City"] || "",
        state: business["Business State"] || business["State"] || "",
        zipCode: business["Business Zip"] || business["Zip Code"] || "",
        phoneNumber: business["Business Phone #"] || "",
        email: business["Business Email Address"] || "",
        communityRegistration: business["Community Registration"] || "",
      };
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("authToken");
    navigate("/");
  };

  // Helper function to get taxpayer ID with fallback
  const getTaxpayerId = (business) => {
    // First try "Taxpayer" key
    if (business["Taxpayer"] && business["Taxpayer"].trim() !== "") {
      return business["Taxpayer"];
    }
    // Fallback to "Tax Payer" key if "Taxpayer" is empty
    if (business["Tax Payer"] && business["Tax Payer"].trim() !== "") {
      return business["Tax Payer"];
    }
    // Return empty string if neither exists or both are empty
    return "";
  };

  // Helper function to fetch and set taxpayer information
  const fetchAndSetTaxPayerInfo = async (taxpayerId) => {
    try {
      const taxpayerInfo = await fetchTaxPayerInfo(taxpayerId);

      // Save expiration date to localStorage if available
      if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Expiration Date"]) {
        const expirationDate = taxpayerInfo["Business License"]["Expiration Date"];
        const status = taxpayerInfo["Business License"]["Status"];
        localStorage.setItem("businessLicenseExpirationDate", expirationDate);
        localStorage.setItem("businessLicenseStatus", status);

        console.log("Business License Expiration Date saved:", expirationDate);
      } else {
        console.log("No expiration date found in taxpayer info");
        localStorage.removeItem("businessLicenseExpirationDate");
        localStorage.removeItem("businessLicenseStatus");
      }

      // Save Type information if available
      if (taxpayerInfo && taxpayerInfo["Taxpayer Type"]) {
        const taxpayerType = taxpayerInfo["Taxpayer Type"];
        localStorage.setItem("taxpayerType", taxpayerType);
        console.log("Taxpayer Type saved:", taxpayerType);
      } else {
        console.log("No taxpayer type found in taxpayer info");
        localStorage.removeItem("taxpayerType");
      }

      // Save Business License Type if available
      if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Type"]) {
        const businessLicenseType = taxpayerInfo["Business License"]["Type"];
        localStorage.setItem("businessLicenseType", businessLicenseType);
        console.log("Business License Type saved:", businessLicenseType);

        // Dispatch custom event for same-tab updates
        dispatchLocalStorageChange();
      } else {
        console.log("No business license type found in taxpayer info");
        localStorage.removeItem("businessLicenseType");
        dispatchLocalStorageChange();
      }

      // Save address information if available
      if (taxpayerInfo) {
        const addressParts = [];

        if (taxpayerInfo["Address"]) {
          addressParts.push(taxpayerInfo["Address"]);
        }
        if (taxpayerInfo["City"]) {
          addressParts.push(taxpayerInfo["City"]);
        }
        if (taxpayerInfo["State"]) {
          addressParts.push(taxpayerInfo["State"]);
        }
        if (taxpayerInfo["Zip"]) {
          addressParts.push(taxpayerInfo["Zip"]);
        }

        if (addressParts.length > 0) {
          const fullAddress = addressParts.join(", ");
          localStorage.setItem("businessAddress", fullAddress);
          console.log("Business Address saved:", fullAddress);
        } else {
          console.log("No address information found in taxpayer info");
          localStorage.removeItem("businessAddress");
        }
      }
    } catch (error) {
      console.error("Failed to fetch taxpayer information:", error);
      // Even if taxpayer info fetch fails, we still proceed with business selection
      localStorage.removeItem("businessLicenseExpirationDate");
      localStorage.removeItem("businessLicenseStatus");
      localStorage.removeItem("businessAddress");
      localStorage.removeItem("taxpayerType");
      localStorage.removeItem("businessLicenseType");
      dispatchLocalStorageChange();
    }
  };

  useEffect(() => {
    // Initialize StyleFolder on mount
    setStyleFolder(getStyleFolder());

    const fetchProfileData = async () => {
      try {
        const response = await fetch(UTILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: new URLSearchParams({ action: "prof" }),
        });

        const data = await response.json();
        console.log("fetchProfileData:", data);

        if (data.status === "error" && data.message === "not logged in") {
          handleLogout();
          return;
        }

        if (response.ok) {
          // ARRAY DATA (business data)
          if (Array.isArray(data)) {
            setBusinessData(data);

            // Check localStorage for selectedTaxPayer
            const storedTaxPayer = localStorage.getItem("selectedTaxPayer");
            let idx = 0;
            if (storedTaxPayer) {
              console.log("Stored Tax Payer found:", storedTaxPayer);
              const foundIdx = data.findIndex(
                (biz) => getTaxpayerId(biz) === storedTaxPayer
              );
              if (foundIdx !== -1) idx = foundIdx;
            }
            console.log("Setting selected business index to:", idx);
            setSelectedBusinessIndex(idx);

            const chosenBusiness = data[idx] || {};
            const currentStyleFolder = getStyleFolder();
            setProfileData(buildProfileData(chosenBusiness, currentStyleFolder));

            // Save Tribal license number if available (for both stored and new taxpayers)
            if (chosenBusiness["Business License"] && Array.isArray(chosenBusiness["Business License"]) && chosenBusiness["Business License"].length > 0) {
              const tribalLicenseNumber = chosenBusiness["Business License"][0]["Tribal license number"];
              if (tribalLicenseNumber) {
                localStorage.setItem("tribalLicenseNumber", tribalLicenseNumber);
                console.log("Tribal license number saved:", tribalLicenseNumber);
              } else {
                localStorage.removeItem("tribalLicenseNumber");
              }
            } else {
              localStorage.removeItem("tribalLicenseNumber");
            }

            // Set Tax Payer in localStorage if not set
            if (!storedTaxPayer) {
              const taxpayerId = getTaxpayerId(chosenBusiness);
              if (taxpayerId) {
                localStorage.setItem("selectedTaxPayer", taxpayerId);
                localStorage.setItem("selectedBusinessName", chosenBusiness["Business Name"] || "");
                localStorage.setItem("selectedBusinessAddress", chosenBusiness["Business Address "] || chosenBusiness["Business Address"] || "");
                localStorage.setItem("selectedBusinessCity", chosenBusiness["Business City"] || "");
                localStorage.setItem("selectedBusinessState", chosenBusiness["Business State"] || "");
                localStorage.setItem("selectedBusinessZip", chosenBusiness["Business Zip"] || "");
                localStorage.setItem("selectedTaxPayerType", chosenBusiness["Tax Payer Type"]);

                // Fetch and set additional taxpayer information including license expiration date
                await fetchAndSetTaxPayerInfo(taxpayerId);
              } else {
                console.warn("No taxpayer ID found for selected business");
              }
            }
          } else {
            // SINGLE PROFILE DATA
            setBusinessData(null);
            setProfileData({
              firstName: data["First Name"],
              middleName: data["Middle Name"],
              lastName: data["Last Name"],
              address: data["Mailing Address"] || data["Address"],
              city: data["Mailing City"] || data["City"],
              state: data["Mailing State"] || data["State"],
              zipCode: data["Mailing Zip"] || data["Zip"],
              phoneNumber: data["Mobile Phone Number"] || data["Home Phone"],
              email: data["Email"],
              communityRegistration: data["Community Registration"] || "",
            });
          }
        } else {
          throw new Error(data.message || "Failed to fetch profile data");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Dropdown Change Handler
  const handleBusinessChange = async (e) => {
    const index = +e.target.value;
    setSelectedBusinessIndex(index);
    const selectedBusiness = businessData[index];

    setProfileData(buildProfileData(selectedBusiness, styleFolder));
    console.log("Selected business:", selectedBusiness);

    const taxpayerId = getTaxpayerId(selectedBusiness);
    console.log("Taxpayer ID:", taxpayerId);
    if (taxpayerId) {
      localStorage.setItem("selectedTaxPayer", taxpayerId);
      localStorage.setItem("selectedBusinessName", selectedBusiness["Business Name"] || "");
      localStorage.setItem("selectedBusinessAddress", selectedBusiness["Business Address "] || selectedBusiness["Business Address"] || selectedBusiness["Address"] || "");
      localStorage.setItem("selectedBusinessCity", selectedBusiness["City"] || selectedBusiness["Business City"] || "");
      localStorage.setItem("selectedBusinessState", selectedBusiness["State"] || selectedBusiness["Business State"] || "");
      localStorage.setItem("selectedBusinessZip", selectedBusiness["Zip Code"] || selectedBusiness["Business Zip"] || "");
      localStorage.setItem("selectedTaxPayerType", selectedBusiness["Tax Payer Type"]);

      // Save Tribal license number if available
      if (selectedBusiness["Business License"] && Array.isArray(selectedBusiness["Business License"]) && selectedBusiness["Business License"].length > 0) {
        const tribalLicenseNumber = selectedBusiness["Business License"][0]["Tribal license number"];
        if (tribalLicenseNumber) {
          localStorage.setItem("tribalLicenseNumber", tribalLicenseNumber);
          console.log("Tribal license number saved:", tribalLicenseNumber);
        } else {
          localStorage.removeItem("tribalLicenseNumber");
        }
      } else {
        localStorage.removeItem("tribalLicenseNumber");
      }

      // Fetch and set additional taxpayer information including license expiration date and types
      try {
        const taxpayerInfo = await fetchTaxPayerInfo(taxpayerId);

        // Save expiration date to localStorage if available
        if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Expiration Date"]) {
          const expirationDate = taxpayerInfo["Business License"]["Expiration Date"];
          const status = taxpayerInfo["Business License"]["Status"];
          localStorage.setItem("businessLicenseExpirationDate", expirationDate);
          localStorage.setItem("businessLicenseStatus", status);
          console.log("Business License Expiration Date saved:", expirationDate);
        } else {
          console.log("No expiration date found in taxpayer info");
          localStorage.removeItem("businessLicenseExpirationDate");
          localStorage.removeItem("businessLicenseStatus");
        }

        // Save Type information if available
        if (taxpayerInfo && taxpayerInfo["Taxpayer Type"]) {
          const taxpayerType = taxpayerInfo["Taxpayer Type"];
          localStorage.setItem("taxpayerType", taxpayerType);
          console.log("Taxpayer Type saved:", taxpayerType);
        } else {
          console.log("No taxpayer type found in taxpayer info");
          localStorage.removeItem("taxpayerType");
        }

        // Save Business License Type if available
        if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Type"]) {
          const businessLicenseType = taxpayerInfo["Business License"]["Type"];
          localStorage.setItem("businessLicenseType", businessLicenseType);
          console.log("Business License Type saved:", businessLicenseType);

          // Dispatch custom event for same-tab updates
          dispatchLocalStorageChange();
        } else {
          console.log("No business license type found in taxpayer info");
          localStorage.removeItem("businessLicenseType");
          dispatchLocalStorageChange();
        }

        // Save address information if available
        if (taxpayerInfo) {
          const addressParts = [];

          if (taxpayerInfo["Address"]) {
            addressParts.push(taxpayerInfo["Address"]);
          }
          if (taxpayerInfo["City"]) {
            addressParts.push(taxpayerInfo["City"]);
          }
          if (taxpayerInfo["State"]) {
            addressParts.push(taxpayerInfo["State"]);
          }
          if (taxpayerInfo["Zip"]) {
            addressParts.push(taxpayerInfo["Zip"]);
          }

          if (addressParts.length > 0) {
            const fullAddress = addressParts.join(", ");
            localStorage.setItem("businessAddress", fullAddress);
            console.log("Business Address saved:", fullAddress);
          } else {
            console.log("No address information found in taxpayer info");
            localStorage.removeItem("businessAddress");
          }
        }
      } catch (error) {
        console.error("Failed to fetch taxpayer information:", error);
        localStorage.removeItem("businessLicenseExpirationDate");
        localStorage.removeItem("businessLicenseStatus");
        localStorage.removeItem("businessAddress");
        localStorage.removeItem("taxpayerType");
        localStorage.removeItem("businessLicenseType");
        dispatchLocalStorageChange();
      }

      toast.success(
        `This business name '${selectedBusiness["Business Name"] || `Business ${index + 1}`}' is set for profile.`
      );
    } else {
      console.warn("No taxpayer ID found for selected business");
      toast.error("No taxpayer ID found for selected business");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <ToastContainer />
      {businessData && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-1">Select Business</label>
          <select
            className="w-full p-3 border border-gray-300 rounded-lg"
            value={selectedBusinessIndex}
            onChange={handleBusinessChange}
          >
            {businessData.map((business, idx) => (
              <option key={idx} value={idx}>
                {business["Business Name"] || `Business ${idx + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* First Name */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">First Name</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.firstName)}
          </div>
        </div>
        {/* Middle Name - hidden for CRIT */}
        {styleFolder !== "crit" && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1">Middle Name</label>
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
              {displayData(profileData?.middleName)}
            </div>
          </div>
        )}
        {/* Last Name */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Last Name</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.lastName)}
          </div>
        </div>
        {/* Address */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Address</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.address)}
          </div>
        </div>
        {/* City */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">City</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.city)}
          </div>
        </div>
        {/* State */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">State</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.state ? getStateName(profileData.state) : null)}
          </div>
        </div>
        {/* Zip Code */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Zip Code</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.zipCode)}
          </div>
        </div>
        {/* Phone Number */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Phone Number</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.phoneNumber)}
          </div>
        </div>
        {/* Email */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Email</label>
          <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
            {displayData(profileData?.email)}
          </div>
        </div>
        {/* Community Registration - only for ccth StyleFolder */}
        {styleFolder === "ccth" && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1">
              Community Registration
            </label>
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
              {displayData(profileData?.communityRegistration)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisplayProfile;